import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import * as yup from 'yup';
import { Alert, AlertTitle, Button, Box, Typography } from '@mui/material';
import Loader from 'components/common/Loader';
import Link from 'components/common/Link';
import notify from 'lib/notify';
import { sendApiRequest } from 'lib/api/public';
import Uploader from 'components/common/Uploader';
import EmailsManager from 'components/common/EmailsManager';
import PhonesManager from 'components/common/PhonesManager';
import SocialMediaLinksManager from 'components/common/SocialMediaLinksManager';
import { renderField } from 'components/utils';
import CustomFieldsForm from 'components/common/CustomFieldsForm';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { FormTextField, scrollToFirstFormErrorOnInvalid } from 'components/common/form';
import LocationFormField from 'components/common/LocationFormField';
import Group from 'components/common/Group';
import Modal from 'components/common/Modal';
import StackTabs from 'components/common/StackTabs';
import MassImportLink from 'components/common/MassImportLink';
import { normalizeCustomFieldValues } from 'components/common/customFields/customFields';
import type { CustomFieldValues } from 'components/common/customFields/types';
import type { Candidate, CandidateFormData, EditableCandidate } from './types';

import {
  useAutoSave,
  useEntitySimpleFieldAutosave,
  AutoSaveStatusIndicator,
  normalizeAttachmentsForAutosave,
} from '../../hooks/autoSave';
import { boxStyle } from 'components/job/JobForm';

const schema = yup
  .object({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    title: yup.string(),
    emails: yup.array().of(
      yup.object({
        type: yup.string().required(),
        email: yup.string().email('Please enter a valid email address').required(),
      }),
    ),
    primaryEmail: yup.string().email('Please enter a valid email address'),
    phones: yup.array().of(
      yup.object({
        type: yup.string(),
        phone: yup.string(),
      }),
    ),
    currentCompany: yup.string(),
    socialMediaLinks: yup.array().of(yup.string()),
    address: yup.object({
      street: yup.string(),
      city: yup.string(),
      state: yup.string(),
    }),
    resumes: yup.array().of(
      yup.object({
        name: yup.string(),
        url: yup.string(),
        date: yup.date(),
      }),
    ),
    attachments: yup.array().of(
      yup.object({
        name: yup.string(),
        url: yup.string(),
        date: yup.date(),
      }),
    ),
    customFieldsValues: yup.object(),
  })
  .required();

const getAutosaveFormSnapshot = (values: CandidateFormData, customFields: CustomFieldValues) => ({
  ...values,
  resumes: normalizeAttachmentsForAutosave(values.resumes),
  attachments: normalizeAttachmentsForAutosave(values.attachments),
  customFieldsValues: customFields,
});

const getAutosaveValidationFields = (changedKeys: string[]) => {
  const fields: string[] = [];

  for (const key of changedKeys) {
    if (key === 'firstName' || key === 'lastName' || key === 'emails' || key === 'primaryEmail') {
      fields.push(key);
    }
  }

  return fields;
};

type Props = {
  onClose: () => void;
  onSuccess: (candidate?: EditableCandidate) => void;
  open: boolean;
  object?: EditableCandidate;
};

const CandidateForm = ({ open, onClose, onSuccess, object }: Props) => {
  const router = useRouter();
  const query = router.query;

  const candidateO = object || ({} as Candidate);
  const isUpdate = Boolean(candidateO._id);

  const [customFieldsValues, setCustomFieldsValues] = useState<CustomFieldValues>(
    normalizeCustomFieldValues(candidateO.customFieldsValues),
  );
  const [emailsHash, setEmailsHash] = useState(Math.random().toString());
  const [phonesHash, setPhonesHash] = useState(Math.random().toString());
  const [addressHash, setAddressHash] = useState(Math.random().toString());
  const [attachmentsHash, setAttachmentsHash] = useState(Math.random().toString());
  const [socialMediaLinksHash, setSocialMediaLinksHash] = useState(Math.random().toString());
  const [duplicate, setDuplicate] = useState<Candidate | null>(null);

  const [tabValue, setTabValue] = useState(0);
  const [saving, setSaving] = useState(false);
  const [parsingCV, setParsingCV] = useState(false);

  const [isParsed, setIsParsed] = useState(false);

  const { control, handleSubmit, setValue, watch, getValues, trigger } = useForm<
    CandidateFormData,
    unknown,
    yup.InferType<typeof schema>
  >({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: candidateO.firstName,
      lastName: candidateO.lastName,
      title: candidateO.title,
      address: candidateO.address,
      currentCompany: candidateO.currentCompany,
      emails: candidateO.emails,
      phones: candidateO.phones,
      primaryEmail: candidateO.primaryEmail,
      socialMediaLinks: candidateO.socialMediaLinks,
      resumes: candidateO.resumes || [],
      attachments: candidateO.attachments || [],
    },
  });

  useEffect(() => {
    if (query.formTab) {
      setTabValue(Number(query.formTab));
    }
  }, [query.formTab]);

  // Watch all form values for auto-save
  const watchedValues = watch();

  const autoSave = useAutoSave({
    enabled: !isUpdate,
    type: 'candidate',
    data: {
      ...watchedValues,
      customFieldsValues,
    },
    onGetDraft: (draft: CandidateFormData & { clientName?: string }) => {
      if (!isUpdate && draft) {
        setValue('firstName', draft.firstName);
        setValue('lastName', draft.lastName);
        setValue('title', draft.title);
        setValue('emails', draft.emails);
        setValue('primaryEmail', draft.primaryEmail);
        setValue('phones', draft.phones);
        setValue('currentCompany', draft.currentCompany);
        setValue('socialMediaLinks', draft.socialMediaLinks);
        setValue('address', draft.address);
        setValue('resumes', draft.resumes);
        setValue('attachments', draft.attachments);
        setCustomFieldsValues(normalizeCustomFieldValues(draft.customFieldsValues));

        setEmailsHash(Math.random().toString());
        setPhonesHash(Math.random().toString());
        setAddressHash(Math.random().toString());
        setAttachmentsHash(Math.random().toString());
        setSocialMediaLinksHash(Math.random().toString());
      }
    },
  });

  const formValues = watch();
  const serializedValues = JSON.stringify(getAutosaveFormSnapshot(formValues, customFieldsValues));

  const seedSnapshot = useCallback(
    () => JSON.stringify(getAutosaveFormSnapshot(getValues(), customFieldsValues)),
    [getValues, customFieldsValues],
  );

  const getValidationFields = useCallback((changedKeys: string[]) => getAutosaveValidationFields(changedKeys), []);

  const updateAutosave = useEntitySimpleFieldAutosave({
    enabled: isUpdate,
    isReady: isUpdate,
    snapshot: serializedValues,
    seedSnapshot,
    isBlocked: () => saving,
    getValidationFields,
    trigger,
    updatePath: '/candidates/update-simple-field',
    idField: 'candidateId',
    entityId: candidateO._id,
  });

  const onSave = async (data: yup.InferType<typeof schema>) => {
    setSaving(true);

    try {
      const response = (await sendApiRequest({
        path: '/candidates/create',
        method: 'POST',
        data: {
          ...data,
          customFieldsValues,
        },
      })) as Candidate;

      onSuccess(response);

      await autoSave.deleteDraft('candidate');

      setSaving(false);
    } catch (error) {
      setSaving(false);
      notify(error);
    }
  };

  const parseCV = async (url: string) => {
    setParsingCV(true);
    setIsParsed(false);
    setDuplicate(null);

    setValue('firstName', '');
    setValue('lastName', '');
    setValue('title', '');
    setValue('currentCompany', '');
    setValue('emails', []);
    setValue('primaryEmail', '');
    setValue('phones', []);
    setValue('socialMediaLinks', []);
    setValue('address', {});
    setValue('attachments', []);
    setCustomFieldsValues({});

    setEmailsHash(Math.random().toString());
    setPhonesHash(Math.random().toString());
    setSocialMediaLinksHash(Math.random().toString());
    setAddressHash(Math.random().toString());
    setAttachmentsHash(Math.random().toString());

    try {
      const response = (await sendApiRequest({
        path: '/candidates/parse-cv',
        method: 'POST',
        data: {
          url,
        },
      })) as {
        currentCompany?: string;
        title?: string;
        firstName?: string;
        lastName?: string;
        emails?: { type: string; email: string }[];
        phones?: { type: string; phone: string }[];
        socialMediaLinks?: string[];
        city?: string;
        state?: string;
        duplicate?: Candidate | null;
      };

      if (response.duplicate) {
        setDuplicate(response.duplicate);
      }

      if (response.currentCompany) setValue('currentCompany', response.currentCompany);
      if (response.title) setValue('title', response.title);
      if (response.firstName) setValue('firstName', response.firstName);
      if (response.lastName) setValue('lastName', response.lastName);
      if (response.emails) {
        setValue('emails', response.emails);
        setEmailsHash(Math.random().toString());
      }
      if (response.phones) {
        setValue('phones', response.phones);
        setPhonesHash(Math.random().toString());
      }
      if (response.socialMediaLinks) {
        setValue('socialMediaLinks', response.socialMediaLinks);
        setSocialMediaLinksHash(Math.random().toString());
      }
      if (response.city || response.state) {
        setValue('address', {
          city: response.city || '',
          state: response.state || '',
        });
        setAddressHash(Math.random().toString());
      }

      setIsParsed(true);
    } catch (error) {
      notify(error);
    } finally {
      setParsingCV(false);
    }
  };

  const renderFields = () => {
    const attachments = watch('attachments');
    const flexOptions = {
      label: 3,
      value: 9,
    };

    return (
      <>
        <Group>
          {renderField({
            id: 'candidate_form_first_name',
            label: 'First Name',
            required: true,
            flexOptions,
            renderControl: () => (
              <FormTextField name="firstName" control={control} fullWidth testId="@candidate-first-name" />
            ),
          })}

          {renderField({
            id: 'candidate_form_last_name',
            label: 'Last Name',
            required: true,
            flexOptions,
            renderControl: () => (
              <FormTextField name="lastName" control={control} fullWidth testId="@candidate-last-name" />
            ),
          })}
        </Group>

        <Group bg="white">
          {renderField({
            id: 'candidate_form_title',
            label: 'Title',
            flexOptions,
            renderControl: () => <FormTextField name="title" control={control} fullWidth />,
          })}

          {renderField({
            id: 'candidate_form_emails',
            label: 'Emails',
            flexOptions,
            renderControl: () => (
              <EmailsManager
                key={emailsHash}
                initialEmails={(watch('emails') || []).map(email => ({
                  type: email.type || 'personal',
                  email: email.email || '',
                }))}
                initialPrimaryEmail={watch('primaryEmail')}
                onChangeEmails={value => setValue('emails', value)}
                onChangePrimaryEmail={value => setValue('primaryEmail', value)}
              />
            ),
          })}

          {renderField({
            id: 'candidate_form_phones',
            label: 'Phones',
            flexOptions,
            renderControl: () => (
              <PhonesManager
                key={phonesHash}
                initialPhones={(watch('phones') || []).map(phone => ({
                  type: phone.type || 'personal',
                  phone: phone.phone || '',
                }))}
                onChange={value => setValue('phones', value)}
              />
            ),
          })}

          {renderField({
            id: 'candidate_form_social_media_links',
            label: 'Social Media Links',
            flexOptions,
            renderControl: () => (
              <SocialMediaLinksManager
                key={socialMediaLinksHash}
                initialLinks={(watch('socialMediaLinks') || []).filter(
                  (link): link is string => typeof link === 'string',
                )}
                onChange={value => {
                  setValue('socialMediaLinks', value);
                }}
              />
            ),
          })}

          <LocationFormField
            key={addressHash}
            control={control}
            flexOptions={flexOptions}
            cityName="address.city"
            stateName="address.state"
          />

          <br />

          {renderField({
            id: 'candidate_form_current_employer',
            label: 'Current employer',
            flexOptions,
            renderControl: () => <FormTextField name="currentCompany" control={control} fullWidth />,
          })}

          {renderField({
            id: 'candidate_form_attachments',
            label: 'Attachments',
            flexOptions,
            renderControl: () => {
              return (
                <Uploader
                  key={attachmentsHash}
                  label="Upload files"
                  defaultFiles={(attachments || []).map(attachment => ({
                    name: attachment.name,
                    url: attachment.url,
                    date: attachment.date,
                  }))}
                  onChange={results => setValue('attachments', Array.isArray(results) ? results : [results])}
                />
              );
            },
          })}
        </Group>

        <CustomFieldsForm
          groupId="candidate_form_custom_fields"
          type="candidate"
          initialValues={customFieldsValues}
          onChange={values => setCustomFieldsValues(values)}
        />
      </>
    );
  };

  const renderTabContent = () => {
    if (tabValue === 0) {
      return (
        <Box>
          {renderResumeToFetchDetails()}

          {isParsed && renderFields()}
        </Box>
      );
    }

    return renderFields();
  };

  const renderContent = () => {
    if (candidateO._id) {
      return renderFields();
    }

    const tabs = [{ label: 'Create from resume', value: 0, testId: '@candidate-add-from-resume' }];

    if (!isParsed) {
      tabs.push({ label: 'Create manually', value: 1, testId: '@candidate-add-manually' });
    }

    return (
      <StackTabs
        noSpacing
        tabs={tabs}
        tabValue={tabValue}
        setTabValue={setTabValue}
        renderTabContent={renderTabContent}
      />
    );
  };

  const renderResumeToFetchDetails = () => {
    return (
      <Box sx={boxStyle}>
        <MassImportLink type="Candidates" />

        {parsingCV && (
          <Box sx={{ marginBottom: 2 }}>
            <Loader />
            <Typography data-testid="@candidate-parsing-cv">Parsing CV...</Typography>
          </Box>
        )}

        <Alert severity="success" sx={{ mb: 1, mt: 2 }}>
          <AlertTitle>Upload a resume file</AlertTitle>
          <Uploader
            label="Upload a file"
            multiple={false}
            testId="@candidate-upload-resume"
            onChange={result => {
              if (Array.isArray(result)) {
                return;
              }

              setValue('resumes', [result]);

              if (result.url) {
                void parseCV(result.url);
              }
            }}
          />
        </Alert>
      </Box>
    );
  };

  const renderUpdateAutoSave = () => (
    <AutoSaveStatusIndicator
      status={updateAutosave.status}
      lastAutoSave={updateAutosave.lastAutoSave}
      type="candidate"
    />
  );

  const renderCreateAutoSave = () => {
    if (tabValue === 1) {
      return (
        <AutoSaveStatusIndicator
          status={autoSave.status}
          lastAutoSave={autoSave.lastAutoSave}
          type="candidate"
          onDiscard={() => {
            setValue('title', '');
            setValue('firstName', '');
            setValue('lastName', '');
            setValue('emails', []);
            setValue('phones', []);
            setValue('socialMediaLinks', []);
            setValue('address', {});
            setValue('currentCompany', '');
            setValue('attachments', []);
            setValue('resumes', []);
            setValue('primaryEmail', '');

            setCustomFieldsValues({});

            setEmailsHash(Math.random().toString());
            setPhonesHash(Math.random().toString());
            setAddressHash(Math.random().toString());
            setAttachmentsHash(Math.random().toString());
            setSocialMediaLinksHash(Math.random().toString());

            autoSave.resetStatus();
          }}
        />
      );
    }
  };

  return (
    <Modal
      onClose={onClose}
      onSubmit={isUpdate ? undefined : handleSubmit(onSave, scrollToFirstFormErrorOnInvalid)}
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {candidateO.firstName || candidateO.lastName
            ? `${candidateO.firstName || ''} ${candidateO.lastName || ''}`
            : 'Add candidate'}
          {isUpdate ? renderUpdateAutoSave() : renderCreateAutoSave()}
        </Box>
      }
      open={open}
      fullScreen
      sx={{
        width: {
          xs: '100%',
          md: '902px',
        },
        left: 'inherit',
      }}
      leftFooterActions={
        <Box>
          {duplicate && (
            <Alert severity="warning" sx={{ mb: '-5px', padding: '1px 10px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Potential duplicate:
                </Typography>
                <Link
                  href={`/candidate?id=${duplicate._id}`}
                  target="_blank"
                  sx={{
                    color: 'secondary.main',
                    textDecoration: 'underline',
                    fontWeight: 500,
                  }}
                >
                  {duplicate.firstName} {duplicate.lastName}
                </Link>
              </Box>
            </Alert>
          )}
        </Box>
      }
      renderActions={() => {
        if (isUpdate) {
          return null;
        }

        if (tabValue === 1 || isParsed) {
          return (
            <Button
              data-testid="@candidate-form-submit"
              variant="contained"
              color="primary"
              disabled={saving}
              type="submit"
            >
              Add
            </Button>
          );
        }
      }}
      renderContent={() => renderContent()}
    />
  );
};

export default CandidateForm;
